import {
  onCleanup,
  createEffect,
  createMemo,
  createRoot,
  onMount,
  on,
  createSignal,
} from 'https://cdn.jsdelivr.net/npm/solid-js@1.7.3/+esm';
import { insert } from 'https://cdn.jsdelivr.net/npm/solid-js@1.7.3/web/+esm';
import { createStore } from 'https://cdn.jsdelivr.net/npm/solid-js@1.7.3/store/+esm';
import { register, compose } from 'https://cdn.jsdelivr.net/npm/component-register@0.6.7/+esm';

// Utility custom element functions

function createProps(raw) {
  const keys = Object.keys(raw);
  const props = {};
  for (let i = 0; i < keys.length; i++) {
    const [get, set] = createSignal(raw[keys[i]]);
    Object.defineProperty(props, keys[i], {
      get() {
        return get();
      },
      set(v) {
        set(() => v);
      },
    });
  }
  return props;
}

function lookupContext(el) {
  if (el.assignedSlot && el.assignedSlot._$owner) return el.assignedSlot._$owner;
  let next = el.parentNode;
  while (next && !next._$owner && !(next.assignedSlot && next.assignedSlot._$owner)) next = next.parentNode;
  return next && next.assignedSlot ? next.assignedSlot._$owner : el._$owner;
}

function withSolid(ComponentType) {
  return (rawProps, options) => {
    const { element } = options;
    return createRoot((dispose) => {
      const props = createProps(rawProps);
      element.addPropertyChangedCallback((key, val) => (props[key] = val));
      element.addReleaseCallback(() => {
        element.renderRoot.textContent = '';
        dispose();
      });
      const comp = ComponentType(props, options);
      return insert(element.renderRoot, comp);
    }, lookupContext(element));
  };
}

function withNoShadowDOM(ComponentType = (props, options) => {}) {
  return (props, options) => {
    const { element } = options;
    element.renderRoot = element;
    return ComponentType(props, options);
  };
}

const customShadowlessElement = (tagName, props = {}, Component = (props, options) => {}, ...rest) =>
  compose(register(tagName, props), withNoShadowDOM, withSolid, ...rest)(Component);

function syncOnClick(slider, featured) {
  return function (e) {
    const index = parseInt(e.target.closest('button').getAttribute('data-index'));
    const relativeIndex = Math.floor(index / featured.trueConfig.slidesToScroll);
    const dif = featured.stateIndex - relativeIndex;
    if (dif > 0) return featured.prev(dif);
    return featured.next(Math.abs(dif));
  };
}

function syncOnSlideWith(slider, target) {
  return function (pageIndex) {
    const dif = target.stateIndex - pageIndex;
    if (dif > 0) return target.prev(dif);
    return target.next(Math.abs(dif));
  };
}

function updateActiveThumbnails(slider, thumbnails) {
  const [pageStart, pageEnd] = slider.states[0].page;

  const activeElements = Array.from(thumbnails.slides).slice(pageStart, pageEnd + 1);

  activeElements.forEach((el) => {
    el.setAttribute('data-active', true);
  });

  return function (pageIndex) {
    const [pageStart, pageEnd] = slider.states[pageIndex].page;
    const [prevPageStart, prevPageEnd] = slider.states[slider.prevStateIndex].page;
    // console.log('PREV: ' + prevPageStart, 'NEW: ' + pageStart);

    const prevActiveElements = Array.from(thumbnails.slides).slice(prevPageStart, prevPageEnd + 1);
    const activeElements = Array.from(thumbnails.slides).slice(pageStart, pageEnd + 1);

    prevActiveElements.forEach((el) => {
      el.setAttribute('data-active', false);
    });

    activeElements.forEach((el) => {
      el.setAttribute('data-active', true);
    });
  };
}

function createKeyboardNavigation(slider) {
  return function (e) {
    const code = e.keyCode ? e.keyCode : e.which;
    const focusedIndex = Array.from(slider.slides).findIndex((s) => s === document.activeElement);

    if (code !== 9 || focusedIndex < 0) return;
    const relativeIndex = Math.floor(focusedIndex / slider.config.slidesToScroll);
    const dif = slider.stateIndex - relativeIndex;

    if (dif > 0) return slider.prev(dif);
    return slider.next(Math.abs(dif));
  };
}

function setupVariantGallery(props, { element, state }) {
  const selectedTemplates = createMemo(() => {
    if (!props.selectedVariant) return null;
    const featured = element
      .querySelector(`template[data-id="${props.selectedVariant}"][type="featured"]`)
      .content.cloneNode(true).children;
    const thumbnails = element
      .querySelector(`template[data-id="${props.selectedVariant}"][type="thumbnail"]`)
      .content.cloneNode(true).children;
    return {
      featured,
      thumbnails,
    };
  });

  createEffect(
    on(
      () => props.rootTarget,
      (target) => {
        const root = element.closest(`#${target}`);
        if (!root) return;

        function updateAttribute(e) {
          if (e.target.name !== 'id') return;
          return element.setAttribute('selected-variant', e.target.value);
        }

        root.addEventListener('change', updateAttribute);

        return onCleanup(() => {
          root.removeEventListener('change', updateAttribute);
        });
      }
    )
  );

  createEffect(
    on(
      () => ({ templates: selectedTemplates() }),
      async ({ templates }) => {
        if (!state.featured || !state.thumbnails || !templates.featured || !templates.thumbnails) return;
        // update featured
        const newFeatured = Array.from(templates.featured).concat(
          Array.from(state.featured.slides).filter((el) => !el.id.includes('variant-'))
        );
        state.featured.track.replaceChildren(...newFeatured);

        // update thumbnails
        const newThumbnails = Array.from(templates.thumbnails).concat(
          Array.from(state.thumbnails.slides).filter((el) => !el.id.includes('variant-'))
        );
        state.thumbnails.track.replaceChildren(...newThumbnails);

        state.thumbnails.el.setAttribute('total-slides', newThumbnails.length);
        state.featured.el.setAttribute('total-slides', newFeatured.length);

        // refresh sliders
        setTimeout(() => {
          state.featured.refresh();
          state.thumbnails.refresh();
          updateActiveThumbnails(state.featured, state.thumbnails)(0);
        });
      }
    )
  );
}

function PhotoGallery(props, { element }) {
  const [state, setState] = createStore({
    thumbnails: null,
    featured: null,
  });

  if (props.selectedVariant && props.rootTarget) setupVariantGallery(props, { element, state });

  onMount(() => {
    if (!props.thumbnailsTarget || !props.featuredTarget) return;
    const updateThumbnailState = (e) => {
      setState('thumbnails', e.detail.slider);
    };

    const updateFeaturedState = (e) => {
      setState('featured', e.detail.slider);
    };

    element.addEventListener(`blaze-slider.loaded.${props.thumbnailsTarget}`, updateThumbnailState);
    element.addEventListener(`blaze-slider.loaded.${props.featuredTarget}`, updateFeaturedState);

    onCleanup(() => {
      element.removeEventListener(`blaze-slider.loaded.${props.thumbnailsTarget}`, updateThumbnailState);
      element.removeEventListener(`blaze-slider.loaded.${props.featuredTarget}`, updateFeaturedState);
    });
  });

  createEffect(
    on(
      () => ({ featured: state.featured, thumbnails: state.thumbnails }),
      ({ thumbnails, featured }) => {
        if (!thumbnails || !featured) return;

        const handleClick = syncOnClick(thumbnails, featured);
        thumbnails.track.addEventListener('click', handleClick);
        onCleanup(() => {
          thumbnails.track.removeEventListener('click', handleClick);
        });

        const handleKeyboardNavigation = createKeyboardNavigation(thumbnails);

        thumbnails.el.addEventListener('keyup', handleKeyboardNavigation);

        featured.onSlide(syncOnSlideWith(featured, thumbnails));
        thumbnails.onSlide(syncOnSlideWith(thumbnails, featured));

        featured.onSlide(updateActiveThumbnails(featured, thumbnails));

        onCleanup(() => {
          thumbnails.el.removeEventListener('keyup', handleKeyboardNavigation);
        });
      }
    )
  );
}

customShadowlessElement(
  'photo-gallery',
  { thumbnailsTarget: '', featuredTarget: '', selectedVariant: '', rootTarget: '' },
  PhotoGallery
);
